import { Chip, Tooltip } from '@material-ui/core'
import React, { useContext } from 'react'

import {
  formatWhen,
  slaTone,
  slaUrgency,
  Ticket
} from '../../helpers/ticketWorkflow'
import { getTranslate } from '../../localization'

interface Props {
  ticket: Pick<Ticket, 'state' | 'sla'>
  /** Injected in tests so the rendering is not clock-dependent. */
  now?: Date
}

// The queue owns one minute clock for ordering. Rows subscribe here so a clock
// tick updates only the small SLA chip subtree, even when the surrounding row
// is memoized.
export const SLAClockContext = React.createContext<Date | null>(null)

/**
 * Renders an SLA badge ONLY when the ticket is breached or close to it.
 *
 * A chip on every row would put a badge on the majority of a healthy queue,
 * and a badge that is always present is one nobody reads — which costs the
 * exact signal the SLA exists to carry. Silence here means "on track".
 */
export default function SLAChip({ ticket, now }: Props) {
  const clock = useContext(SLAClockContext)
  const urgency = slaUrgency(ticket, now ?? clock ?? new Date())
  if (urgency === 'none' || urgency === 'on-track') return null

  const label =
    urgency === 'breached' ? getTranslate('Breached') : getTranslate('Due soon')

  // The deadline itself goes in the tooltip rather than the label: the chip
  // answers "does this need attention", the tooltip answers "by when".
  return (
    <Tooltip
      title={`${getTranslate('Respond by')} ${formatWhen(
        ticket.sla!.respondBy
      )}`}>
      <Chip
        size="small"
        label={label}
        color={slaTone(urgency)}
        style={{ marginInlineStart: 8 }}
      />
    </Tooltip>
  )
}
